import { Link } from "react-router-dom";
import { BookOpen, FileCheck, Stamp, ScrollText } from "lucide-react";
import { Seo } from "../components/Seo";
import { Button } from "../components/ui/button";
import { ServiceCTA } from "../components/LeadCta";

export function Registration() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-10" data-testid="registration-page">
      <Seo title="Registration Services Gujarat" description="Document registration, pre-registration, SRO finder, stamp duty and appointment guidance in Gujarat." />
      <h1 className="font-display font-bold text-3xl md:text-4xl flex items-center gap-3"><ScrollText className="text-primary" size={32} /> Registration Services / રજિસ્ટ્રેશન સેવાઓ</h1>
      <p className="text-muted-foreground mt-2">દસ્તાવેજ નોંધણી માટે સંપૂર્ણ માહિતી અને સહાય — Complete guidance for document registration in Gujarat.</p>

      <div className="mt-8 space-y-6">
        <section className="bg-white border border-border rounded-xl p-6">
          <h2 className="font-display font-semibold text-xl flex items-center gap-2"><FileCheck className="text-secondary" size={20} /> Document Registration / દસ્તાવેજ નોંધણી</h2>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Sale deed, gift deed, mortgage, lease and other instruments must be registered at the Sub-Registrar Office (SRO) under the Registration Act.
            In Gujarat this happens through GARVI 2.0 — pre-registration online, then execution at the SRO.
            <br /><br />ગુજરાતમાં વેચાણ દસ્તાવેજ, ભેટ દસ્તાવેજ વગેરે SRO ખાતે GARVI 2.0 દ્વારા નોંધાય છે. અમે પ્રી-રજિસ્ટ્રેશન, ફી ગણતરી અને એપોઈન્ટમેન્ટમાં સહાય કરીએ છીએ.
          </p>
        </section>
        <section className="bg-white border border-border rounded-xl p-6">
          <h2 className="font-display font-semibold text-xl flex items-center gap-2"><Stamp className="text-secondary" size={20} /> Stamp Duty & Registration Fee</h2>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Stamp duty is charged on the higher of market value or jantri value; registration fee is charged additionally.
            Use our <Link to="/calculators" className="text-primary font-semibold">calculators</Link> for instant estimates.
          </p>
        </section>
        <section className="bg-white border border-border rounded-xl p-6">
          <h2 className="font-display font-semibold text-xl flex items-center gap-2"><BookOpen className="text-secondary" size={20} /> Required Documents / જરૂરી દસ્તાવેજો</h2>
          <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc pl-5">
            <li>Identity proof of all parties (Aadhaar / PAN)</li>
            <li>Title documents — previous registered deed, 7/12 or property card</li>
            <li>Index-2 of previous transaction</li>
            <li>Property tax receipts / NA order where applicable</li>
            <li>Draft deed prepared as per Gujarat registration rules</li>
          </ul>
        </section>
        <section className="bg-muted rounded-xl p-6 flex items-center justify-between flex-wrap gap-4">
          <p className="font-semibold">Need registration assistance in Rajkot or anywhere in Gujarat?</p>
          <Button className="bg-primary" asChild data-testid="registration-contact-btn"><Link to="/contact">Contact Us</Link></Button>
        </section>
      </div>
    </div>
  );
}

export function EStamping() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-10" data-testid="estamping-page">
      <Seo title="E-Stamping Gujarat" description="What is e-stamping, how it works, required details and step-by-step process in Gujarat." />
      <h1 className="font-display font-bold text-3xl md:text-4xl flex items-center gap-3"><Stamp className="text-primary" size={32} /> E-Stamping / ઈ-સ્ટેમ્પિંગ</h1>
      <p className="text-muted-foreground mt-2">E-stamping is the secure, digital way of paying stamp duty — replacing physical stamp papers.</p>
      <div className="mt-8 space-y-6">
        {[
          ["What is e-stamping? / ઈ-સ્ટેમ્પિંગ શું છે?", "E-stamping is a computer-generated certificate that proves stamp duty payment. It carries a unique certificate number and cannot be tampered with, unlike physical stamp paper."],
          ["How it works", "1. Share document type and consideration amount. 2. Stamp duty is calculated. 3. Payment is made through authorized channels. 4. E-stamp certificate is generated with a unique number. 5. Attach it to your agreement/deed before registration or execution."],
          ["Required details", "Document/agreement type, party names, property description, consideration value, and the district where it will be used."],
          ["Agreement types", "Rent agreements, affidavits, partnership deeds, sale agreements, loan agreements and most instruments requiring stamp duty."],
        ].map(([h, b]) => (
          <section key={h} className="bg-white border border-border rounded-xl p-6">
            <h2 className="font-display font-semibold text-xl">{h}</h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{b}</p>
          </section>
        ))}
        <section className="bg-muted rounded-xl p-6">
          <p className="font-semibold">DocGalaxy provides e-stamping information and assistance. We are not a government department.</p>
          <p className="text-sm text-muted-foreground mt-2">
            Related guides: <Link to="/blog/e-stamping-near-me" className="text-primary font-semibold">E-Stamping near me</Link> ·{" "}
            <Link to="/blog/how-to-get-online-e-stamp-paper-rajkot" className="text-primary font-semibold">Online e-stamp paper in Rajkot</Link> ·{" "}
            <Link to="/blog/stamp-duty-gujarat-rates" className="text-primary font-semibold">Stamp duty rates Gujarat</Link> ·{" "}
            <Link to="/blog/garvi-2-guide-gujarat" className="text-primary font-semibold">Garvi 2.0 guide</Link>
          </p>
          <Button className="bg-primary mt-3" asChild data-testid="estamping-contact-btn"><Link to="/contact">Get Assistance</Link></Button>
        </section>
        <ServiceCTA service="E-Stamping" />
      </div>
    </div>
  );
}

export function Garvi() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-10" data-testid="garvi-page">
      <Seo title="GARVI 2.0 Guide Gujarat" description="GARVI 2.0 overview, user registration, pre-registration steps, FAQ and common errors." />
      <h1 className="font-display font-bold text-3xl md:text-4xl">GARVI 2.0 Guide</h1>
      <p className="text-muted-foreground mt-2">GARVI 2.0 is the Government of Gujarat's online document pre-registration system. This is an educational section — DocGalaxy is not the government portal.</p>
      <div className="mt-8 space-y-6">
        {[
          ["Overview", "GARVI 2.0 lets you prepare document registration online: enter party and property details, upload drafts, calculate fees and book an SRO appointment before visiting in person."],
          ["User registration", "Create an account on the GARVI 2.0 portal with your mobile number and email. Verify via OTP, complete your profile with Aadhaar/PAN details."],
          ["Document pre-registration — step by step", "1. Login and choose document type. 2. Fill party details (executant/claimant). 3. Enter property details (district, taluka, village, survey/CTS number). 4. System calculates stamp duty and registration fee. 5. Upload draft deed. 6. Book SRO appointment. 7. Visit SRO with originals for execution."],
          ["Common errors & fixes", "Name mismatch with Aadhaar — correct before payment. Wrong survey number — verify against 7/12 first. Payment failed but amount debited — wait for auto-reversal before retrying."],
          ["FAQ", "Is GARVI mandatory? For most SRO registrations in Gujarat, pre-registration through GARVI is required. Can DocGalaxy do it for me? We assist you through the process; execution requires your physical presence with witnesses at the SRO."],
        ].map(([h, b]) => (
          <section key={h} className="bg-white border border-border rounded-xl p-6">
            <h2 className="font-display font-semibold text-xl">{h}</h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{b}</p>
          </section>
        ))}
        <section className="bg-muted rounded-xl p-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="font-semibold">Stuck with GARVI 2.0? Our Rajkot team can guide you.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Full Gujarati guide: <Link to="/blog/garvi-2-guide-gujarat" className="text-primary font-semibold" data-testid="garvi-hub-link">Garvi 2.0 Login, Appointment &amp; Registration (ગુજરાતી)</Link>
            </p>
          </div>
          <Button className="bg-primary" asChild data-testid="garvi-contact-btn"><Link to="/contact">Get Help</Link></Button>
        </section>
        <ServiceCTA service="Garvi 2.0 Assistance" />
      </div>
    </div>
  );
}
